import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Typography,
    Button,
    Space,
    message,
    Spin,
    Breadcrumb,
    Alert,
    Modal,
    Steps,
    Row,
    Col,
    Dropdown,
    Checkbox
} from 'antd';
import {
    HomeOutlined,
    EditOutlined,
    ArrowLeftOutlined,
    EyeOutlined,
    PlusOutlined,
    CheckCircleOutlined,
    FormOutlined,
    PictureOutlined,
    RocketOutlined,
    MoreOutlined
} from '@ant-design/icons';
import auctionService from '../../services/auctionService';
import DashboardLayout from '../Dashboard/DashboardLayout';
import ImageUpload from './ImageUpload';

const { Title, Text } = Typography;

const AuctionImages = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [auction, setAuction] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth < 576);

    useEffect(() => {
        loadAuction();
        // Check if user has previously chosen not to show the modal
        const dontShow = localStorage.getItem('dontShowAuctionImagesSuccessModal');
        if (dontShow === 'true') {
            setShowSuccessModal(false);
        }
    }, [slug]);

    // Handle window resize for responsive design
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            setIsSmallMobile(window.innerWidth < 576);
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Initial check

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loadAuction = async () => {
        try {
            const response = await auctionService.getAuction(slug);
            setAuction(response.data);
        } catch (error) {
            console.error('Error loading auction:', error);
            message.error('Failed to load auction details');
        } finally {
            setLoading(false);
        }
    };

    const handleDontShowAgain = (e) => {
        setDontShowAgain(e.target.checked);
        if (e.target.checked) {
            localStorage.setItem('dontShowAuctionImagesSuccessModal', 'true');
        } else {
            localStorage.removeItem('dontShowAuctionImagesSuccessModal');
        }
    };

    const handleImageUploadSuccess = () => {
        setShowSuccess(true);
        if (!localStorage.getItem('dontShowAuctionImagesSuccessModal')) {
            setShowSuccessModal(true);
        }
    };

    const handleCreateLots = () => {
        setShowSuccessModal(false);
        navigate(`/lots/create?auction_slug=${slug}`);
    };

    // Menu items for mobile dropdown
    const actionMenuItems = [
        {
            key: 'my-auctions',
            label: 'My Auctions',
            icon: <ArrowLeftOutlined />,
            onClick: () => navigate('/my-auctions')
        },
        {
            key: 'view-auction',
            label: 'View Auction',
            icon: <EyeOutlined />,
            onClick: () => navigate(`/auctions/${slug}`)
        },
        {
            key: 'edit-auction',
            label: 'Edit Auction',
            icon: <EditOutlined />,
            onClick: () => navigate(`/auctions/${slug}/edit`)
        },
        {
            key: 'create-lots',
            label: 'Create Lots',
            icon: <PlusOutlined />,
            onClick: () => navigate(`/lots/create?auction_slug=${slug}`)
        }
    ];

    return (
        <DashboardLayout>
            <Spin spinning={loading}>
                <div style={{ padding: '0 16px' }}>
                    <Breadcrumb
                        style={{ 
                            marginBottom: '24px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        <Breadcrumb.Item>
                            <Link to="/dashboard">
                                <HomeOutlined /> Dashboard
                            </Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <Link to="/my-auctions">My Auctions</Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            {auction?.title || 'Auction Images'}
                        </Breadcrumb.Item>
                    </Breadcrumb>

                    <div style={{ marginTop: 16 }}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <Row gutter={[16, 16]} align="middle">
                                <Col xs={24} sm={24} md={12} lg={12}>
                                    <Title 
                                        level={2} 
                                        style={{ 
                                            margin: 0,
                                            fontSize: 'clamp(20px, 4vw, 24px)'
                                        }}
                                    >
                                        Auction Images
                                    </Title>
                                </Col>
                                <Col xs={24} sm={24} md={12} lg={12}>
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'flex-end',
                                        flexWrap: 'wrap',
                                        gap: '8px'
                                    }}>
                                        {/* Desktop buttons */}
                                        {!isMobile && (
                                            <Space size="small" wrap>
                                                {/* <Button 
                                                    icon={<ArrowLeftOutlined />}
                                                    onClick={() => navigate('/my-auctions')}
                                                >
                                                    My Auctions
                                                </Button> */}
                                                <Button 
                                                    icon={<EyeOutlined />}
                                                    onClick={() => navigate(`/auctions/${slug}`)}
                                                >
                                                    View Auction
                                                </Button>
                                                <Button 
                                                    type="primary"
                                                    icon={<EditOutlined />}
                                                    onClick={() => navigate(`/auctions/${slug}/edit`)}
                                                >
                                                    Edit Auction
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    icon={<PlusOutlined />}
                                                    onClick={() => navigate(`/lots/create?auction_slug=${slug}`)}
                                                >
                                                    Create Lots
                                                </Button>
                                            </Space>
                                        )}
                                        
                                        {/* Mobile dropdown */}
                                        {isMobile && (
                                            <Dropdown
                                                menu={{ items: actionMenuItems }}
                                                trigger={['click']}
                                                placement="bottomRight"
                                            >
                                                <Button icon={<MoreOutlined />}>
                                                    Actions
                                                </Button>
                                            </Dropdown>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            {auction?.status === 'completed' && (
                                <Alert
                                    message="Auction Completed"
                                    description="This auction is already completed. You cannot upload images to completed auctions."
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: 12 }}
                                />
                            )}

                            {showSuccess && (
                                <Alert
                                    message="Images uploaded successfully!"
                                    description={
                                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                            <span>Great! Your auction images have been uploaded. Would you like to start creating lots for this auction?</span>
                                            <Button
                                                type="primary"
                                                icon={<PlusOutlined />}
                                                onClick={() => navigate(`/lots/create?auction_slug=${slug}`)}
                                            >
                                                Create Lots Now
                                            </Button>
                                        </Space>
                                    }
                                    type="success"
                                    showIcon
                                    closable
                                    onClose={() => setShowSuccess(false)}
                                    style={{ marginBottom: 12 }}
                                />
                            )}

                            <ImageUpload
                                entityType="auction"
                                entitySlug={slug}
                                onSuccess={handleImageUploadSuccess}
                                showDragger={true}
                                disabled={auction?.status === 'completed'}
                            />
                        </Space>
                    </div>
                </div>

                <Modal
                    title={
                        <Space>
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 'clamp(20px, 4vw, 24px)' }} />
                            <span style={{ fontSize: 'clamp(16px, 3vw, 18px)' }}>Images Uploaded Successfully!</span>
                        </Space>
                    }
                    open={showSuccessModal}
                    onCancel={() => setShowSuccessModal(false)}
                    footer={[
                        <Checkbox 
                            key="dontShow" 
                            checked={dontShowAgain}
                            onChange={handleDontShowAgain}
                            style={{ 
                                float: isSmallMobile ? 'none' : 'left',
                                display: isSmallMobile ? 'block' : 'inline-block',
                                width: isSmallMobile ? '100%' : 'auto',
                                marginBottom: isSmallMobile ? '8px' : '0'
                            }}
                        >
                            Don't show this again
                        </Checkbox>,
                        <Button 
                            key="back" 
                            onClick={() => setShowSuccessModal(false)}
                            block={isSmallMobile}
                            style={{ 
                                marginBottom: isSmallMobile ? '8px' : '0'
                            }}
                        >
                            Continue Managing Images
                        </Button>,
                        <Button 
                            key="submit" 
                            type="primary" 
                            icon={<RocketOutlined />}
                            onClick={handleCreateLots}
                            block={isSmallMobile}
                        >
                            Start Creating Lots
                        </Button>
                    ]}
                    width={isSmallMobile ? '95%' : 600}
                    style={{ top: isSmallMobile ? 20 : undefined }}
                >
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Text style={{ fontSize: 'clamp(14px, 2.5vw, 16px)' }}>
                            Great job! Your auction images have been uploaded successfully. Now it's time to create lots for your auction.
                        </Text>

                        <Steps
                            direction={isSmallMobile ? "horizontal" : "vertical"}
                            current={0}
                            items={[
                                {
                                    title: 'Create Lots',
                                    description: isSmallMobile ? '' : 'Add detailed information about each item in your auction, including descriptions, specifications, and starting prices.',
                                    icon: <FormOutlined />
                                },
                                {
                                    title: 'Add Lot Images',
                                    description: isSmallMobile ? '' : 'Upload high-quality images for each lot to showcase your items effectively.',
                                    icon: <PictureOutlined />
                                }
                            ]}
                            style={{ 
                                width: '100%',
                                overflowX: isSmallMobile ? 'auto' : 'visible'
                            }}
                        />

                        <Alert
                            message="Pro Tip"
                            description="Creating detailed lot descriptions and adding multiple high-quality images can significantly increase bidder interest and final sale prices."
                            type="info"
                            showIcon
                            style={{ fontSize: 'clamp(12px, 2vw, 14px)' }}
                        />
                    </Space>
                </Modal>
            </Spin>
        </DashboardLayout>
    );
};

export default AuctionImages; 